import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Product, CartItem, CustomerProfile, SearchFilters, Customer, LoginResponse, PublicSettings } from '@petmas/shared';
import { HTTP_STATUS, ONE_HOUR_MS, FIVE_MINUTES_MS, THIRTY_SECONDS_MS } from '@petmas/shared';

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

// Auth token storage keys
const AUTH_TOKEN_KEY = 'petmas_auth_token';
const REFRESH_TOKEN_KEY = 'petmas_refresh_token';
const TOKEN_EXPIRES_KEY = 'petmas_token_expires';
const USER_DATA_KEY = 'petmas_user_data';
const ADMIN_TOKEN_KEY = 'petmas_admin_token';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

class ApiClient {
  private baseUrl: string;
  private sessionId: string | null = null;
  public onUnauthorized: (() => void) | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.initializeSession();
  }

  private initializeSession(): void {
    if (typeof window === 'undefined') return;

    this.sessionId = localStorage.getItem('petmas_session_id');
    if (!this.sessionId) {
      this.sessionId = crypto.randomUUID();
      localStorage.setItem('petmas_session_id', this.sessionId);
    }
  }

  private getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  private getAdminToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(ADMIN_TOKEN_KEY);
  }

  /**
   * Fire-and-forget tracking request.
   * Does not wait for response or throw errors - used for analytics events.
   */
  trackEvent(endpoint: string, data: Record<string, unknown>): void {
    const url = `${this.baseUrl}${endpoint}`;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).catch(() => {
      // Silently ignore tracking failures - this is intentional
    });
  }

  /**
   * Make an admin-authenticated API request.
   * Uses the admin token from localStorage.
   */
  async adminRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const adminToken = this.getAdminToken();
    if (!adminToken) {
      throw new Error('No admin token');
    }

    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${adminToken}`,
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json() as ApiResponse<T>;

    if (!response.ok || !data.success) {
      throw new Error(data.error?.message ?? 'API error');
    }

    return data.data as T;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const authToken = this.getAuthToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(this.sessionId && { 'X-Session-ID': this.sessionId }),
      ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized
    if (response.status === HTTP_STATUS.UNAUTHORIZED) {
      if (this.onUnauthorized) {
        this.onUnauthorized();
      }
      throw new Error('Unauthorized');
    }

    const data = await response.json() as ApiResponse<T>;

    if (!data.success || !data.data) {
      throw new Error(data.error?.message ?? 'Request failed');
    }

    return data.data;
  }

  // Auth methods
  async login(email: string, password: string): Promise<LoginResponse> {
    return this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async loginWithGoogle(idToken: string): Promise<LoginResponse> {
    return this.request<LoginResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });
  }

  async register(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<{ customerId: string; message: string }> {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async refreshToken(refreshToken: string): Promise<{
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
  }> {
    return this.request('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  }

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    await this.request('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  }

  async getCurrentCustomer(): Promise<Customer> {
    return this.request<Customer>('/auth/me');
  }

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return this.request('/auth/password/reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return this.request('/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return this.request('/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    return this.request('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  // Admin Auth methods
  async adminRefreshToken(refreshToken: string): Promise<{
    accessToken: string;
    expiresAt: number;
  }> {
    return this.request('/admin/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  }

  async adminLogin(email: string, password: string): Promise<{
    admin: unknown;
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
  }> {
    return this.request('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async adminLogout(token: string, refreshToken: string): Promise<void> {
    const headers: HeadersInit = {
      'Authorization': `Bearer ${token}`,
    };
    await this.request('/admin/auth/logout', {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken }),
    });
  }

  // Products
  async getProducts(params?: {
    category?: string;
    brand?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
    page?: number;
    pageSize?: number;
    sortBy?: string;
  }): Promise<PaginatedResponse<Product>> {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.set('category', params.category);
    if (params?.brand) searchParams.set('brand', params.brand);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.minPrice !== undefined) searchParams.set('minPrice', String(params.minPrice));
    if (params?.maxPrice !== undefined) searchParams.set('maxPrice', String(params.maxPrice));
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);

    const query = searchParams.toString();
    return this.request<PaginatedResponse<Product>>(
      `/products${query ? `?${query}` : ''}`
    );
  }

  async getProduct(id: string): Promise<Product> {
    return this.request<Product>(`/products/${id}`);
  }

  async searchProducts(query: string, filters?: SearchFilters): Promise<PaginatedResponse<Product>> {
    return this.request<PaginatedResponse<Product>>('/products/search', {
      method: 'POST',
      body: JSON.stringify({ query, filters }),
    });
  }

  async getFilterOptions(category?: string): Promise<{
    brands: Array<{ value: string; label: string; count: number }>;
    priceRanges: Array<{ value: string; label: string; min: number; max: number | null }>;
  }> {
    const query = category ? `?category=${encodeURIComponent(category)}` : '';
    return this.request(`/products/filters${query}`);
  }

  async getStockByLocation(variantId: string): Promise<{
    variantId: string;
    totalAvailable: number;
    byLocation: Array<{
      officeId: string;
      officeName: string;
      quantity: number;
      quantityReserved: number;
      quantityAvailable: number;
    }>;
  }> {
    return this.request(`/products/variants/${variantId}/stock`);
  }

  // Cart
  async getCart(): Promise<{ items: CartItem[]; total: number }> {
    return this.request('/cart');
  }

  async addToCart(variantId: number, quantity: number): Promise<{ items: CartItem[] }> {
    return this.request('/cart/items', {
      method: 'POST',
      body: JSON.stringify({ variantId, quantity }),
    });
  }

  async updateCartItem(variantId: number, quantity: number): Promise<{ items: CartItem[] }> {
    return this.request(`/cart/items/${variantId}`, {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    });
  }

  async removeFromCart(variantId: number): Promise<{ items: CartItem[] }> {
    return this.request(`/cart/items/${variantId}`, {
      method: 'DELETE',
    });
  }

  async validateCart(): Promise<{ valid: boolean; errors: Array<{ variantId: string; message: string }> }> {
    return this.request('/cart/validate', { method: 'POST' });
  }

  // Checkout
  async getShippingOptions(): Promise<Array<{ id: number; name: string; price: number }>> {
    return this.request('/checkout/shipping-options');
  }

  async getPaymentOptions(): Promise<Array<{ id: number; name: string; isVirtual: boolean }>> {
    return this.request('/checkout/payment-options');
  }

  async getCheckoutSummary(shippingTypeId?: number): Promise<{
    subtotal: number;
    discount: number;
    shipping: number;
    tax: number;
    total: number;
  }> {
    const query = shippingTypeId ? `?shippingTypeId=${shippingTypeId}` : '';
    return this.request(`/checkout/summary${query}`);
  }

  async initiateCheckout(data: {
    shippingTypeId: number;
    paymentTypeId: number;
    customerId?: string;
    shippingAddressId?: string;
    notes?: string;
  }): Promise<{ orderId: string; checkoutUrl?: string }> {
    return this.request('/checkout', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCheckoutStatus(checkoutId: string): Promise<{
    status: 'pending' | 'processing' | 'completed' | 'failed';
    orderId?: string;
  }> {
    return this.request(`/checkout/${checkoutId}/status`);
  }

  // Customer
  async getCustomerProfile(): Promise<CustomerProfile | null> {
    try {
      return await this.request<CustomerProfile>('/customer/profile');
    } catch {
      return null;
    }
  }

  async updateCustomerProfile(data: Partial<CustomerProfile>): Promise<CustomerProfile> {
    return this.request('/customer/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // AI Chat
  async sendChatMessage(
    message: string,
    conversationId?: string,
    context?: {
      fingerprint: string;
      customerId?: string;
      currentPage: string;
      currentProductId?: string;
      sessionDuration: number;
      recentActions: Array<{
        type: 'page_view' | 'product_view' | 'search' | 'add_to_cart' | 'remove_from_cart';
        timestamp: number;
        data: Record<string, unknown>;
      }>;
      deviceType: string;
      browser: string;
    }
  ): Promise<{
    response: string;
    conversationId: string;
    products?: Product[];
    intent?: string;
    followUpQuestions?: Array<{ id: string; question: string; options?: string[] }>;
  }> {
    return this.request('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ message, conversationId, context }),
    });
  }

  async getRecommendations(productId?: string): Promise<Product[]> {
    const query = productId ? `?productId=${productId}` : '';
    return this.request<Product[]>(`/ai/recommendations${query}`);
  }

  async getPageContext(page: string, productId?: string): Promise<{
    pageType: string;
    welcomeMessage: string;
    quickActions: string[];
    proactivePrompt?: string;
  }> {
    const params = new URLSearchParams({ page });
    if (productId) params.set('productId', productId);
    return this.request(`/ai/page-context?${params.toString()}`);
  }

  // Customer Intelligence
  async mergeCustomerProfile(
    fingerprintHash: string,
    customerId: string,
    customerEmail?: string
  ): Promise<{
    merged: boolean;
    message: string;
    totalSessions?: number;
  }> {
    return this.request('/intelligence/merge', {
      method: 'POST',
      body: JSON.stringify({ fingerprintHash, customerId, customerEmail }),
    });
  }

  async syncCustomerProfile(
    fingerprintHash: string,
    customerId?: string,
    deviceMetadata?: {
      deviceType: string;
      browser: string;
      os: string;
      screenResolution: string;
      timezone: string;
      language: string;
    }
  ): Promise<{
    profile: {
      id: string;
      fingerprintHash: string;
      customerId?: string;
      sessionCount: number;
      firstSeenAt: string;
      lastSeenAt: string;
    } | null;
  }> {
    return this.request('/intelligence/profile', {
      method: 'POST',
      body: JSON.stringify({ fingerprintHash, customerId, deviceMetadata }),
    });
  }

  // Stock Notifications
  async subscribeToStockNotification(data: {
    email: string;
    variantId: number;
    productId: string;
    productName: string;
  }): Promise<{ id: string; message: string }> {
    return this.request('/notifications/stock', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async unsubscribeFromStockNotification(email: string, variantId: number): Promise<{ message: string }> {
    return this.request(`/notifications/stock?email=${encodeURIComponent(email)}&variantId=${variantId}`, {
      method: 'DELETE',
    });
  }

  async getMyStockNotifications(email: string): Promise<Array<{
    id: string;
    variantId: number;
    productId: string;
    productName: string;
    createdAt: string;
  }>> {
    return this.request(`/notifications/stock?email=${encodeURIComponent(email)}`);
  }

  // Public Settings (no auth required)
  async getPublicSettings(): Promise<PublicSettings> {
    return this.request<PublicSettings>('/settings/public');
  }
}

export const apiClient = new ApiClient(API_BASE_URL);

// Export auth storage keys for use in AuthContext
export { AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY, TOKEN_EXPIRES_KEY, USER_DATA_KEY, ADMIN_TOKEN_KEY };

// React Query hooks

export function useProducts(params?: {
  category?: string;
  brand?: string;
  search?: string;
  minPrice?: number;
  maxPrice?: number;
  page?: number;
  pageSize?: number;
  sortBy?: string;
}) {
  return useQuery({
    queryKey: ['products', params],
    queryFn: () => apiClient.getProducts(params),
  });
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: ['product', id],
    queryFn: () => apiClient.getProduct(id),
    enabled: Boolean(id),
  });
}

export function useSearchProducts(query: string, filters?: SearchFilters) {
  return useQuery({
    queryKey: ['search', query, filters],
    queryFn: () => apiClient.searchProducts(query, filters),
    enabled: query.length >= 2,
  });
}

export function useFilterOptions(category?: string) {
  return useQuery({
    queryKey: ['filters', category],
    queryFn: () => apiClient.getFilterOptions(category),
    staleTime: FIVE_MINUTES_MS, // 5 minutes
  });
}

export function useStockByLocation(variantId: string | undefined) {
  return useQuery({
    queryKey: ['stock', 'location', variantId],
    queryFn: () => apiClient.getStockByLocation(variantId!),
    enabled: Boolean(variantId),
    staleTime: THIRTY_SECONDS_MS, // 30 seconds - stock changes frequently
  });
}

export function useCustomerProfile() {
  return useQuery({
    queryKey: ['customer', 'profile'],
    queryFn: () => apiClient.getCustomerProfile(),
  });
}

export function useUpdateCustomerProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<CustomerProfile>) =>
      apiClient.updateCustomerProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', 'profile'] });
    },
  });
}

export function useRecommendations(productId?: string) {
  return useQuery({
    queryKey: ['recommendations', productId],
    queryFn: () => apiClient.getRecommendations(productId),
  });
}

export function usePageContext(page: string, productId?: string) {
  return useQuery({
    queryKey: ['ai', 'page-context', page, productId],
    queryFn: () => apiClient.getPageContext(page, productId),
    staleTime: FIVE_MINUTES_MS,
  });
}

export function useSendChatMessage() {
  return useMutation({
    mutationFn: ({
      message,
      conversationId,
      context,
    }: {
      message: string;
      conversationId?: string;
      context?: {
        fingerprint: string;
        customerId?: string;
        currentPage: string;
        currentProductId?: string;
        sessionDuration: number;
        recentActions: Array<{
          type: 'page_view' | 'product_view' | 'search' | 'add_to_cart' | 'remove_from_cart';
          timestamp: number;
          data: Record<string, unknown>;
        }>;
        deviceType: string;
        browser: string;
      };
    }) => apiClient.sendChatMessage(message, conversationId, context),
  });
}

// Settings hook - fetches once and caches for the session
export function useSettings() {
  return useQuery({
    queryKey: ['settings', 'public'],
    queryFn: () => apiClient.getPublicSettings(),
    staleTime: FIVE_MINUTES_MS, // 5 minutes
    gcTime: ONE_HOUR_MS, // 1 hour (renamed from cacheTime in v5)
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
}

// Checkout hooks
export function useShippingOptions() {
  return useQuery({
    queryKey: ['checkout', 'shipping-options'],
    queryFn: () => apiClient.getShippingOptions(),
  });
}

export function usePaymentOptions() {
  return useQuery({
    queryKey: ['checkout', 'payment-options'],
    queryFn: () => apiClient.getPaymentOptions(),
  });
}

export function useCheckoutSummary(shippingTypeId?: number) {
  return useQuery({
    queryKey: ['checkout', 'summary', shippingTypeId],
    queryFn: () => apiClient.getCheckoutSummary(shippingTypeId),
    enabled: shippingTypeId !== undefined,
  });
}

export function useInitiateCheckout() {
  return useMutation({
    mutationFn: (data: {
      shippingTypeId: number;
      paymentTypeId: number;
      customerId?: string;
      shippingAddressId?: string;
      notes?: string;
    }) => apiClient.initiateCheckout(data),
  });
}

export function useValidateCart() {
  return useMutation({
    mutationFn: () => apiClient.validateCart(),
  });
}
